/**
 * Panes people can close, collapse and rearrange, kept per person on the
 * server. A page declares its panes per row (a resizable group); each pane's
 * header carries collapse and close buttons, and the page's Panes menu lists
 * every pane to bring closed ones back, move panes within their row, or reset.
 */
import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Group, Panel, Separator, useDefaultLayout, type PanelImperativeHandle } from 'react-resizable-panels';
import { ArrowLeft, ArrowRight, ChevronsLeftRight, LayoutPanelLeft, Minimize2, RotateCcw, X } from 'lucide-react';
import { useServerPref, layoutStorage } from '../lib/prefs';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

interface PaneDef {
  id: string;
  label: string;
  /** Cannot be closed (the page's main content). */
  required?: boolean;
  defaultSize?: string;
  minSize?: string;
  maxSize?: string;
  render: () => ReactNode;
}

interface LayoutState { hidden: string[]; order: Record<string, string[]> }
const EMPTY: LayoutState = { hidden: [], order: {} };

interface PaneLayout {
  page: string;
  rows: Record<string, PaneDef[]>;
  state: LayoutState;
  /** Every pane of the row in the person's order, closed ones included (the Panes menu's stable order). */
  ordered: (row: string) => PaneDef[];
  visible: (row: string) => PaneDef[];
  /** Whether a pane is shown: always true for a required one (1.2.0: a real boolean, never undefined). */
  isShown: (p: PaneDef) => boolean;
  isHidden: (id: string) => boolean;
  hide: (id: string) => void;
  show: (id: string) => void;
  move: (row: string, id: string, by: -1 | 1) => void;
  reset: () => void;
  customised: boolean;
}

export function usePaneLayout(page: string, rows: Record<string, PaneDef[]>): PaneLayout {
  const [saved, setSaved] = useServerPref<LayoutState | null>(`layout.${page}`, null);
  const state = saved ?? EMPTY;
  // Several changes in a row (move, then show) build on each other, not on a stale render.
  const latest = useRef(state);
  latest.current = state;
  const orderedIn = (s: LayoutState, row: string) => {
    const defs = rows[row] ?? [];
    const order = s.order[row] ?? [];
    return [...defs].sort((a, b) => {
      const ia = order.indexOf(a.id);
      const ib = order.indexOf(b.id);
      return (ia < 0 ? defs.indexOf(a) + 100 : ia) - (ib < 0 ? defs.indexOf(b) + 100 : ib);
    });
  };
  const shownIn = (s: LayoutState, p: PaneDef) => Boolean(p.required) || !s.hidden.includes(p.id);
  const write = (fn: (cur: LayoutState) => LayoutState) => {
    const next = fn(latest.current);
    if (next === latest.current) return;
    latest.current = next;
    setSaved(next);
  };
  return {
    page, rows, state, customised: Boolean(saved),
    ordered: (row) => orderedIn(state, row),
    visible: (row) => orderedIn(state, row).filter((p) => shownIn(state, p)),
    isShown: (p) => shownIn(state, p),
    isHidden: (id) => state.hidden.includes(id),
    hide: (id) => write((cur) => ({ ...cur, hidden: [...new Set([...cur.hidden, id])] })),
    show: (id) => write((cur) => ({ ...cur, hidden: cur.hidden.filter((x) => x !== id) })),
    // Moves past the next shown pane (closed ones keep their places in between).
    move: (row, id, by) => write((cur) => {
      const all = orderedIn(cur, row);
      const shown = all.filter((p) => shownIn(cur, p)).map((p) => p.id);
      const i = shown.indexOf(id);
      const other = shown[i + by];
      if (i < 0 || !other) return cur;
      const ids = all.map((p) => p.id);
      const a = ids.indexOf(id);
      const b = ids.indexOf(other);
      [ids[a], ids[b]] = [ids[b]!, ids[a]!];
      return { ...cur, order: { ...cur.order, [row]: ids } };
    }),
    reset: () => setSaved(null),
  };
}

interface PaneCtx { collapsed: boolean; toggleCollapse: () => void; close: (() => void) | null; label: string }
const PaneContext = createContext<PaneCtx | null>(null);

/** Collapse and close buttons for a pane's header. */
export function PaneControls() {
  const ctx = useContext(PaneContext);
  if (!ctx) return null;
  return (
    <span className="pane-controls">
      <button type="button" className="btn ghost icon sm" aria-label="Collapse pane" title={`Collapse ${ctx.label}`} onClick={ctx.toggleCollapse} data-testid="pane-collapse"><Minimize2 size={12} /></button>
      {ctx.close && <button type="button" className="btn ghost icon sm" aria-label="Close pane" title={`Close ${ctx.label} (reopen from the Panes menu)`} onClick={ctx.close} data-testid="pane-close"><X size={13} /></button>}
    </span>
  );
}

/** One resizable row (or column) of panes, in the person's order, without the closed ones. */
export function PaneGroup({ layout, row, orientation = 'horizontal' }: { layout: PaneLayout; row: string; orientation?: 'horizontal' | 'vertical' }) {
  const panes = layout.visible(row);
  const signature = panes.map((p) => p.id).join('.');
  // A new set or order of panes gets its own remembered sizes.
  return <PaneGroupInner key={signature} layout={layout} panes={panes} storageId={`prism-${layout.page}-${row}-${signature}`} orientation={orientation} />;
}

function PaneGroupInner({ layout, panes, storageId, orientation }: { layout: PaneLayout; panes: PaneDef[]; storageId: string; orientation: 'horizontal' | 'vertical' }) {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({ id: storageId, storage: layoutStorage });
  return (
    <Group orientation={orientation} className="panel-group" defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged}>
      {panes.map((p, i) => (
        <PaneSlot key={p.id} pane={p} layout={layout} separator={i > 0} orientation={orientation} only={panes.length === 1} />
      ))}
    </Group>
  );
}

function PaneSlot({ pane, layout, separator, orientation, only }: { pane: PaneDef; layout: PaneLayout; separator: boolean; orientation: 'horizontal' | 'vertical'; only: boolean }) {
  const ref = useRef<PanelImperativeHandle | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const ctx = useMemo<PaneCtx>(() => ({
    collapsed, label: pane.label,
    toggleCollapse: () => { if (ref.current?.isCollapsed()) ref.current.expand(); else ref.current?.collapse(); },
    close: pane.required ? null : () => layout.hide(pane.id),
  }), [collapsed, pane, layout]);
  return (
    <>
      {separator && <Separator className={`resize-handle ${orientation === 'vertical' ? 'horizontal' : ''}`} />}
      <Panel id={pane.id} panelRef={ref} defaultSize={pane.defaultSize} minSize={pane.minSize} maxSize={pane.maxSize}
        collapsible={!only} collapsedSize="30px" onResize={() => setCollapsed(Boolean(ref.current?.isCollapsed()))}>
        <PaneContext.Provider value={ctx}>
          {collapsed ? (
            <button type="button" className={`pane-collapsed ${orientation}`} onClick={() => ref.current?.expand()} title={`Expand ${pane.label}`} data-testid={`pane-collapsed-${pane.id}`}>
              <ChevronsLeftRight size={13} /><span>{pane.label}</span>
            </button>
          ) : pane.render()}
        </PaneContext.Provider>
      </Panel>
    </>
  );
}

/** The page's Panes menu: show closed panes again, move panes within their row, reset the layout. */
export function PanesMenu({ layout }: { layout: PaneLayout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissOnOutside(ref, open, () => setOpen(false));
  const hiddenCount = Object.values(layout.rows).flat().filter((p) => !p.required && layout.isHidden(p.id)).length;
  return (
    <div className="menu-anchor" ref={ref}>
      <button className="btn ghost sm" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="panes-menu" title="Show, hide and arrange panes">
        <LayoutPanelLeft size={14} /> Panes{hiddenCount ? <span className="count">{hiddenCount} closed</span> : null}
      </button>
      {open && (
        <div className="popover columns-popover" role="dialog" aria-label="Panes">
          {Object.entries(layout.rows).map(([row]) => {
            // A stable order (1.2.0): a closed pane keeps its row, so the next row never slides under the pointer.
            const ordered = layout.ordered(row);
            const shown = layout.visible(row);
            return (
              <ul key={row}>
                {ordered.map((p) => {
                  const on = layout.isShown(p);
                  const idx = shown.findIndex((x) => x.id === p.id);
                  return (
                    <li key={p.id}>
                      <label className="toggle-row"><input type="checkbox" checked={on} disabled={p.required} onChange={() => (on ? layout.hide(p.id) : layout.show(p.id))} data-testid={`pane-toggle-${p.id}`} /><span>{p.label}</span></label>
                      {on && shown.length > 1 && (
                        <span className="col-move">
                          <button className="tl-mini" aria-label={`Move ${p.label} earlier`} disabled={idx <= 0} onClick={() => layout.move(row, p.id, -1)}><ArrowLeft size={11} /></button>
                          <button className="tl-mini" aria-label={`Move ${p.label} later`} disabled={idx >= shown.length - 1} onClick={() => layout.move(row, p.id, 1)}><ArrowRight size={11} /></button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            );
          })}
          <p className="help" style={{ margin: '0 0 6px' }}>Drag the edges between panes to resize; collapse one from its header.</p>
          {layout.customised && <button className="btn ghost sm" onClick={() => layout.reset()} data-testid="panes-reset"><RotateCcw size={12} /> Default layout</button>}
        </div>
      )}
    </div>
  );
}
